import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { setAuth } from "@/lib/auth";
import { inviteContinuationPath } from "@/lib/inviteContinuation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Import logo
import { LOGO_COLLAPSED_DATA } from '../assets/logoInline';


export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [disabledOpen, setDisabledOpen] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      setAuth(res.data.token, res.data.user);
      toast({ title: "Welcome back" });

      // P4-WP-9A.1: the destination is no longer decided here.
      //
      // FIX-013 hard-coded "/app/warroom", which is wrong for any role that
      // cannot reach War Room. Login also CANNOT make the decision correctly:
      // it renders outside TenantProvider, so `usePermissions()` is unavailable
      // and the access context has not been fetched yet at this point.
      //
      // So login sends everyone to "/app" and DefaultHomeRedirect -- which runs
      // after ProtectedLayoutInner has resolved both account context and
      // permissions -- resolves the real destination. The parked
      // `post_login_redirect` deep link is deliberately LEFT in sessionStorage
      // for that resolver to consume, because judging whether it is still
      // reachable requires the same authorization this component lacks.
      const fallbackPath = "/app";
      // P4-WP-8C.7: an invitation part-way through acceptance wins the
      // redirect, so signing in returns to the invitation rather than dropping
      // the user somewhere else with no idea what happened to it. The path is
      // constructed from a constant -- storage supplies only the token -- so
      // this cannot become an open redirect, and it is NOT authorization: the
      // claim endpoint revalidates everything. Unchanged by WP-9A.1.
      const invitePath = inviteContinuationPath();
      const redirectTo = invitePath || fallbackPath;
      navigate(redirectTo, { replace: true });
      setTimeout(() => {
        if (window.location.pathname === "/login") {
          window.location.href = redirectTo;
        }
      }, 300);
      return;
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.detail === "Account disabled") {
        setDisabledOpen(true);
        return;
      }

      toast({
        title: "Login failed",
        description: err?.response?.data?.detail || "Please check your credentials.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="login-page">
      <Dialog open={disabledOpen} onOpenChange={setDisabledOpen}>
        <DialogContent className="glass border-white/10" data-testid="account-disabled-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Account inactive</DialogTitle>
            <DialogDescription className="text-slate-200/70">
              Your account is inactive. Please email support@smguys.com for further information.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
              onClick={() => setDisabledOpen(false)}
              data-testid="account-disabled-ok"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <div className="mx-auto grid min-h-screen max-w-6xl grid-cols-1 gap-8 px-5 py-10 lg:grid-cols-2">
        <div className="flex flex-col justify-center">
          {/* Logo - 56-72px for login hero */}
          <div className="flex items-center gap-4 mb-6">
            {/* P4-WP-8C.7 (logo sizing correction): bounded crop of the asset's own
                transparent padding.

                `LOGO_COLLAPSED_DATA` is a 336x224 canvas whose visible mark
                occupies only left=98 top=67 right=250 bottom=132 -- 152x65, or
                29.0% of the height. A height-driven box therefore renders less
                than a third of itself as ink, which is why `lg:h-28` (112px)
                produced a 32.5px mark. Reaching the intended size by raising
                the height alone would need a ~250px box carrying ~180px of
                empty space, wrecking the auth composition.

                So the wrapper is sized to the VISIBLE aspect (152/65) and the
                image is scaled and offset by percentages derived from that
                bbox, leaving the wrapper filled exactly by the mark:

                  width  336/152 = 221.0526%      left -98/152 = -64.4737%
                  height 224/65  = 344.6154%      top  -67/65  = -103.0769%

                Only transparent padding falls outside the wrapper -- the mark
                itself is never clipped, and the scale stays uniform (the image
                resolves to its native 1.5 aspect), so nothing is distorted.
                The canonical asset is untouched; no transform is used. */}
            <div
              className="relative h-16 overflow-hidden sm:h-[72px] lg:h-20 aspect-[152/65]"
              data-testid="login-logo"
            >
              <img
                src={LOGO_COLLAPSED_DATA}
                alt="MissionAssist"
                className="absolute max-w-none"
                style={{
                  width: "221.0526%",
                  height: "344.6154%",
                  left: "-64.4737%",
                  top: "-103.0769%",
                }}
              />
            </div>
          </div>
          <h1
            className="text-4xl font-semibold tracking-tight text-slate-100 sm:text-5xl lg:text-6xl"
            data-testid="login-hero-title"
          >
            MissionAssist
          </h1>
          <div className="mt-3 text-base font-medium text-slate-100/90 md:text-lg" data-testid="login-hero-line-2">
            Operational Intelligence OS
          </div>
          <div className="mt-4 text-sm text-slate-200/50 md:text-base" data-testid="login-hero-line-3">
            Powered by Assist Engines
          </div>
        </div>

        <div className="flex items-center justify-center lg:justify-end">
          <Card className="w-full max-w-md glass" data-testid="login-card">
            <CardHeader>
              <CardTitle className="text-slate-100" data-testid="login-card-title">
                Login
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                <div className="space-y-2">
                  <label className="text-sm text-slate-200/80" data-testid="login-email-label">
                    Email
                  </label>
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    type="email"
                    autoComplete="email"
                    className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                    data-testid="login-email-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-slate-200/80" data-testid="login-password-label">
                    Password
                  </label>
                  <Input
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    type="password"
                    autoComplete="current-password"
                    className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                    data-testid="login-password-input"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                  data-testid="login-submit-button"
                >
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
                <div className="text-sm text-slate-200/70" data-testid="login-register-hint">
                  New here?{" "}
                  <Link
                    to="/register"
                    className="text-slate-100 underline-offset-4 hover:underline"
                    data-testid="login-register-link"
                  >
                    Create an account
                  </Link>
                  <span className="mx-2 text-slate-200/30" data-testid="login-hint-divider">•</span>
                  <Link
                    to="/forgot-password"
                    className="text-slate-100 underline-offset-4 hover:underline"
                    data-testid="login-forgot-password-hint-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
